import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { DollarSign, TrendingUp, Users, CreditCard, Eye, HelpCircle, Search, X } from 'lucide-react';
import { backendClient } from '@/lib/backendClient';
import { useToast } from '@/hooks/use-toast';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import { useTableSort } from '@/hooks/useTableSort';

interface BillingMetrics {
  totalRevenue: number;
  platformRevenue: number;
  practitionerPayouts: number;
  pendingPayments: number;
  paidPayments: number;
  totalTransactions: number;
}

interface PaymentTransaction {
  id: string;
  amount: number;
  platform_revenue: number;
  practitioner_payout: number;
  payment_status: string;
  payment_method: string;
  transaction_date: string;
  practitioner_name: string;
  session_title: string;
  client_name: string;
}


const BillingManagement: React.FC = () => {
  const [metrics, setMetrics] = useState<BillingMetrics | null>(null);
  const [transactions, setTransactions] = useState<PaymentTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    fetchBillingData();
  }, []);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      console.log('🚀 Fetching billing data...');

      // Fetch payment transactions with complete session, client, and group data
      const { data: transactionsData, error: transactionsError } = await backendClient
        .from('payment_transactions')
        .select(`
          *,
          sessions(
            id,
            title,
            session_scope,
            client_id,
            group_session_id,
            practitioner_clients(id, client_name),
            group_sessions(id, group_name, organization_name)
          )
        `)
        .order('transaction_date', { ascending: false })
        .limit(50);

      if (transactionsError) {
        console.error('Transactions query error:', transactionsError);
        throw transactionsError;
      }

      // Fetch practitioner profiles for names
      const practitionerIds = [...new Set(transactionsData?.map(t => t.practitioner_id).filter(Boolean) || [])];
      const { data: profiles } = await backendClient
        .from('profiles')
        .select('id, full_name, first_name, last_name')
        .in('id', practitionerIds);

      // Process transactions with complete information
      const processedTransactions: PaymentTransaction[] = (transactionsData || []).map(transaction => {
        const profile = profiles?.find(p => p.id === transaction.practitioner_id);
        const session = transaction.sessions;

        // Determine practitioner name
        const practitionerName = transaction.practitioner_id
          ? (profile?.full_name ||
             `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() ||
             'Practitioner (Not Found)')
          : 'Direct User Purchase';

        // Determine session title
        const sessionTitle = session?.title || 'Session (Not Found)';

        // Determine client/recipient name based on session scope
        let clientName = 'N/A';

        if (session) {
          switch (session.session_scope) {
            case 'personal':
              clientName = 'Personal Use';
              break;

            case 'client':
              // Individual client session
              if (session.practitioner_clients) {
                clientName = session.practitioner_clients.client_name;
              } else if (session.client_id) {
                clientName = 'Client (Not Found)';
              } else {
                clientName = 'Individual Client';
              }
              break;

            case 'group':
              // Group session
              if (session.group_sessions) {
                const groupName = session.group_sessions.group_name ||
                                 session.group_sessions.organization_name;
                clientName = `Group: ${groupName}`;
              } else if (session.group_session_id) {
                clientName = 'Group (Not Found)';
              } else {
                clientName = 'Group Session';
              }
              break;

            default:
              clientName = 'Unknown Scope';
          }
        }

        return {
          id: transaction.id,
          amount: transaction.amount || 0,
          platform_revenue: transaction.platform_revenue || 0,
          practitioner_payout: transaction.practitioner_payout || 0,
          payment_status: transaction.payment_status || 'pending',
          payment_method: transaction.payment_method || 'not_set',
          transaction_date: transaction.transaction_date,
          practitioner_name: practitionerName,
          session_title: sessionTitle,
          client_name: clientName
        };
      });

      // Calculate metrics
      const totalRevenue = processedTransactions.reduce((sum, t) => sum + t.amount, 0);
      const platformRevenue = processedTransactions.reduce((sum, t) => sum + t.platform_revenue, 0);
      const practitionerPayouts = processedTransactions.reduce((sum, t) => sum + t.practitioner_payout, 0);
      const pendingPayments = processedTransactions.filter(t => t.payment_status === 'pending').length;
      const paidPayments = processedTransactions.filter(t => t.payment_status === 'paid').length;

      setMetrics({
        totalRevenue,
        platformRevenue,
        practitionerPayouts,
        pendingPayments,
        paidPayments,
        totalTransactions: processedTransactions.length
      });

      setTransactions(processedTransactions);

      console.log('✅ Billing data loaded:', {
        transactions: processedTransactions.length,
        metrics
      });

    } catch (error) {
      console.error('Error fetching billing data:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load billing data.",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">Paid</Badge>;
      case 'pending':
        return <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20">Pending</Badge>;
      case 'failed':
        return <Badge className="bg-red-500/10 text-red-600 border-red-500/20">Failed</Badge>;
      case 'refunded':
        return <Badge className="bg-slate-500/10 text-slate-600 border-slate-500/20">Refunded</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Filter transactions based on search query
  const filteredTransactions = transactions.filter(transaction => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      transaction.session_title.toLowerCase().includes(query) ||
      transaction.client_name.toLowerCase().includes(query) ||
      transaction.practitioner_name.toLowerCase().includes(query) ||
      transaction.payment_status.toLowerCase().includes(query)
    );
  });

  // Table sorting
  const { sortedData, requestSort, getSortIndicator } = useTableSort<PaymentTransaction>(
    filteredTransactions,
    'transaction_date',
    'desc'
  );

  const clearSearch = () => {
    setSearchQuery('');
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Revenue Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Tooltip>
            <TooltipTrigger asChild>
              <Card className="bg-gradient-to-r from-emerald-50 to-emerald-100 border-emerald-200 cursor-help">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-emerald-600 flex items-center gap-1">
                        Total Revenue
                        <HelpCircle className="h-3 w-3 text-emerald-500" />
                      </p>
                      <p className="text-3xl font-bold text-emerald-900">
                        {formatCurrency(metrics?.totalRevenue || 0)}
                      </p>
                    </div>
                    <DollarSign className="h-8 w-8 text-emerald-500" />
                  </div>
                </CardContent>
              </Card>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="font-semibold mb-1">Total Revenue</p>
              <p className="text-sm">All money received from users for sessions and credits. This is the sum of all successful payment transactions.</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200 cursor-help">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-600 flex items-center gap-1">
                        Platform Revenue
                        <HelpCircle className="h-3 w-3 text-blue-500" />
                      </p>
                      <p className="text-3xl font-bold text-blue-900">
                        {formatCurrency(metrics?.platformRevenue || 0)}
                      </p>
                    </div>
                    <TrendingUp className="h-8 w-8 text-blue-500" />
                  </div>
                </CardContent>
              </Card>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="font-semibold mb-1">Platform Revenue (Your Income)</p>
              <p className="text-sm">Money retained by VoiceThera after deducting referral commissions. This is typically 90% of user payments. Formula: Total Revenue - Referral Commissions</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Card className="bg-gradient-to-r from-purple-50 to-purple-100 border-purple-200 cursor-help">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-purple-600 flex items-center gap-1">
                        Referral Commissions
                        <HelpCircle className="h-3 w-3 text-purple-500" />
                      </p>
                      <p className="text-3xl font-bold text-purple-900">
                        {formatCurrency(metrics?.practitionerPayouts || 0)}
                      </p>
                    </div>
                    <Users className="h-8 w-8 text-purple-500" />
                  </div>
                </CardContent>
              </Card>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="font-semibold mb-1">Referral Commissions</p>
              <p className="text-sm">Total commissions earned by practitioners when their referred users make payments. Default rate is 10% of user payments. These are paid out to practitioners monthly.</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Card className="bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200 cursor-help">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-orange-600 flex items-center gap-1">
                        Pending Payments
                        <HelpCircle className="h-3 w-3 text-orange-500" />
                      </p>
                      <p className="text-3xl font-bold text-orange-900">{metrics?.pendingPayments || 0}</p>
                      <p className="text-xs text-orange-600">{metrics?.totalTransactions || 0} total transactions</p>
                    </div>
                    <CreditCard className="h-8 w-8 text-orange-500" />
                  </div>
                </CardContent>
              </Card>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="font-semibold mb-1">Pending Payments</p>
              <p className="text-sm">Number of payment transactions still being processed or awaiting confirmation. These will move to "succeeded" or "failed" status once completed.</p>
            </TooltipContent>
          </Tooltip>
        </div>


      {/* Recent Transactions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Recent Transactions
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchBillingData}
              disabled={loading}
            >
              Refresh Data
            </Button>
          </div>

          {/* Search Bar */}
          {transactions.length > 0 && (
            <div className="flex items-center gap-2 mt-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search transactions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-9"
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                  </button>
                )}
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <div className="text-center py-8">
              <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium text-muted-foreground">No transactions yet</p>
              <p className="text-sm text-muted-foreground">
                Payment transactions will appear here once sessions are created and billed.
              </p>
            </div>
          ) : sortedData.length === 0 ? (
            <div className="text-center py-8">
              <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium text-muted-foreground">No transactions found</p>
              <p className="text-sm text-muted-foreground">
                Try adjusting your search query
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead
                    onSort={() => requestSort('session_title')}
                    sortDirection={getSortIndicator('session_title')}
                  >
                    Session & Client
                  </SortableTableHead>
                  <SortableTableHead
                    onSort={() => requestSort('practitioner_name')}
                    sortDirection={getSortIndicator('practitioner_name')}
                  >
                    Practitioner
                  </SortableTableHead>
                  <SortableTableHead
                    onSort={() => requestSort('amount')}
                    sortDirection={getSortIndicator('amount')}
                  >
                    <Tooltip>
                      <TooltipTrigger className="flex items-center gap-1 cursor-help">
                        Amount
                        <HelpCircle className="h-3 w-3" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-sm">Total amount paid by the user</p>
                      </TooltipContent>
                    </Tooltip>
                  </SortableTableHead>
                  <SortableTableHead
                    onSort={() => requestSort('platform_revenue')}
                    sortDirection={getSortIndicator('platform_revenue')}
                  >
                    <Tooltip>
                      <TooltipTrigger className="flex items-center gap-1 cursor-help">
                        Platform Revenue
                        <HelpCircle className="h-3 w-3" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p className="text-sm">Money retained by VoiceThera (typically 90% of payment)</p>
                      </TooltipContent>
                    </Tooltip>
                  </SortableTableHead>
                  <SortableTableHead
                    onSort={() => requestSort('practitioner_payout')}
                    sortDirection={getSortIndicator('practitioner_payout')}
                  >
                    <Tooltip>
                      <TooltipTrigger className="flex items-center gap-1 cursor-help">
                        Referral Commission
                        <HelpCircle className="h-3 w-3" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p className="text-sm">Commission paid to practitioner who referred this user (10% default)</p>
                      </TooltipContent>
                    </Tooltip>
                  </SortableTableHead>
                  <SortableTableHead
                    onSort={() => requestSort('payment_status')}
                    sortDirection={getSortIndicator('payment_status')}
                  >
                    Status
                  </SortableTableHead>
                  <SortableTableHead
                    onSort={() => requestSort('transaction_date')}
                    sortDirection={getSortIndicator('transaction_date')}
                  >
                    Date
                  </SortableTableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedData.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{transaction.session_title}</p>
                        <p className="text-xs text-muted-foreground">for {transaction.client_name}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="font-medium text-sm">{transaction.practitioner_name}</p>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{formatCurrency(transaction.amount)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-blue-600 font-medium">{formatCurrency(transaction.platform_revenue)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-emerald-600 font-medium">{formatCurrency(transaction.practitioner_payout)}</span>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(transaction.payment_status)}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {new Date(transaction.transaction_date).toLocaleDateString()}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      </div>
    </TooltipProvider>
  );
};

export default BillingManagement;
